import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowleftIcon from "../icons/Arrows/ArrowleftIcon";
import VendorReviewActions from "../components/VendorOnboardingAndLegalApproval/VendorReviewActions";
import VendorReviewDetails from "../components/VendorOnboardingAndLegalApproval/VendorReviewDetails";
import companyController from "../controllers/companyController";
import { userService } from "../services/userService";
import "../styles/VendorReviewView.css";
import { translateLabel, translateText, useTranslationSnapshot } from "./../translations/runtime";


const getStoredToken = () => {
  const storedUser = userService.getJwt();

  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getPayload = (response) => {
  const payload = response?.data;
  return payload?.data ?? payload?.result ?? payload;
};

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent) || "";

const normalizeEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const firstEmailDifferentFrom = (blockedEmail, ...values) =>
  values.find(
    (value) => isPresent(value) && normalizeEmail(value) !== blockedEmail,
  ) || "";

const normalizePhone = (value) => String(value || "").replace(/[^\d+]/g, "");

const formatPhone = (phone, code) => {
  const phoneText = String(phone || "").trim();
  const codeText = String(code || "").trim();

  if (!phoneText) return "";
  if (!codeText || phoneText.startsWith("+")) return phoneText;

  const prefix = codeText.startsWith("+") ? codeText : `+${codeText}`;
  return `${prefix} ${phoneText}`;
};

const firstPhoneDifferentFrom = (blockedPhone, ...values) => {
  const normalizedBlockedPhone = normalizePhone(blockedPhone);

  return (
    values.find((value) => {
      const phone = Array.isArray(value)
        ? formatPhone(value[0], value[1])
        : value;

      return (
        isPresent(phone) &&
        (!normalizedBlockedPhone ||
          normalizePhone(phone) !== normalizedBlockedPhone)
      );
    }) || ""
  );
};

const PHONE_KEY_PATTERN =
  /(phone|mobile|contactnumber|contactno|tel|telephone|whatsapp)/i;
const PHONE_CODE_KEY_PATTERN =
  /(countrycode|phonecode|dialcode|prefix|suffix)/i;

const findPhoneInObject = (source, blockedPhone, depth = 0) => {
  if (!source || typeof source !== "object" || depth > 3) return "";

  const normalizedBlockedPhone = normalizePhone(blockedPhone);

  for (const [key, value] of Object.entries(source)) {
    if (!isPresent(value)) continue;

    const normalizedKey = key.replace(/[^a-z0-9]/gi, "").toLowerCase();

    if (
      PHONE_KEY_PATTERN.test(normalizedKey) &&
      !PHONE_CODE_KEY_PATTERN.test(normalizedKey) &&
      (typeof value === "string" || typeof value === "number")
    ) {
      const phone = String(value).trim();

      if (
        phone &&
        (!normalizedBlockedPhone ||
          normalizePhone(phone) !== normalizedBlockedPhone)
      ) {
        return phone;
      }
    }
  }

  for (const value of Object.values(source)) {
    if (value && typeof value === "object") {
      const phone = findPhoneInObject(value, blockedPhone, depth + 1);
      if (phone) return phone;
    }
  }

  return "";
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{12}$/i;

const getCompanyIdFromRow = (row = {}) =>
  firstDefined(
    row?.companyID,
    row?.companyId,
    row?.company_id,
    row?.company?.companyID,
    row?.company?.companyId,
    row?.company?.id,
    row?.companyDetails?.companyID,
    row?.companyDetails?.companyId,
    row?.companyDetails?.id,
  );

const getBusinessProfileIdFromRow = (row = {}) =>
  firstDefined(
    row?.businessProfileID,
    row?.businessProfileId,
    row?.busienssProfileID,
    row?.busienssProfileId,
    row?.business_profile_id,
    row?.businessProfile?.businessProfileID,
    row?.businessProfile?.businessProfileId,
    row?.businessProfile?.busienssProfileID,
    row?.businessProfile?.busienssProfileId,
    row?.businessProfile?.id,
    row?.company?.businessProfileID,
    row?.company?.businessProfileId,
    row?.company?.busienssProfileID,
    row?.company?.busienssProfileId,
    row?.companyDetails?.businessProfileID,
    row?.companyDetails?.businessProfileId,
    row?.companyDetails?.busienssProfileID,
    row?.companyDetails?.busienssProfileId,
  );

const getApplicationIdFromRow = (row = {}) =>
  firstDefined(
    row?.applicationID,
    row?.applicationId,
    row?.application_id,
    row?.requestID,
    row?.requestId,
    row?.companyRequestID,
    row?.companyRequestId,
  );

const parseJwtPayload = (jwt) => {
  try {
    const token = String(jwt || "").replace(/^Bearer\s+/i, "");
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload) return {};

    const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const decodedPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map(
          (character) =>
            `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`,
        )
        .join(""),
    );

    return JSON.parse(decodedPayload);
  } catch {
    return {};
  }
};

const getCompanyIdFromUserDetails = (userDetails) => {
  if (
    !userDetails ||
    typeof userDetails !== "object" ||
    Array.isArray(userDetails)
  ) {
    return "";
  }

  const companyIdKey = Object.keys(userDetails).find((key) =>
    UUID_PATTERN.test(key),
  );

  if (companyIdKey) return companyIdKey;

  for (const details of Object.values(userDetails)) {
    const companyId = firstDefined(
      details?.companyID,
      details?.companyId,
      details?.company_id,
      details?.company?.companyID,
      details?.company?.companyId,
      details?.company?.id,
    );

    if (companyId) return companyId;
  }

  return "";
};

const getCompanyContextFromJwt = () => {
  const payload = parseJwtPayload(getStoredToken());
  const userDetails = payload.userDetails || {};
  const companyId = firstDefined(
    payload.companyID,
    payload.companyId,
    payload.company_id,
    payload.company?.companyID,
    payload.company?.companyId,
    payload.company?.id,
    payload.primaryCompanyID,
    payload.primaryCompanyId,
    payload.primaryCompany?.companyID,
    payload.primaryCompany?.companyId,
    payload.primaryCompany?.id,
    getCompanyIdFromUserDetails(userDetails),
  );
  const currentCompanyDetails = companyId ? userDetails?.[companyId] : null;

  const businessProfileID = firstDefined(
    payload.businessProfileID,
    payload.businessProfileId,
    payload.busienssProfileID,
    payload.busienssProfileId,
    payload.business_profile_id,
    payload.businessProfile?.businessProfileID,
    payload.businessProfile?.businessProfileId,
    payload.businessProfile?.busienssProfileID,
    payload.businessProfile?.busienssProfileId,
    payload.businessProfile?.id,
    currentCompanyDetails?.businessProfileID,
    currentCompanyDetails?.businessProfileId,
    currentCompanyDetails?.busienssProfileID,
    currentCompanyDetails?.busienssProfileId,
    currentCompanyDetails?.business_profile_id,
  );

  if (businessProfileID) return { companyId, businessProfileID };

  if (userDetails && typeof userDetails === "object") {
    for (const details of Object.values(userDetails)) {
      const businessProfileId = firstDefined(
        details?.businessProfileID,
        details?.businessProfileId,
        details?.busienssProfileID,
        details?.busienssProfileId,
        details?.business_profile_id,
      );

      if (businessProfileId) {
        return { companyId, businessProfileID: businessProfileId };
      }
    }
  }

  return { companyId, businessProfileID: "" };
};

const getStoredUserEmail = () => {
  const payload = userService.getUserPayload() || {};
  const userObject = userService.getUserObject?.() || {};

  return normalizeEmail(
    firstDefined(
      payload.emailAddress,
      payload.email,
      payload.user?.emailAddress,
      payload.user?.email,
      userObject.emailAddress,
      userObject.email,
    ),
  );
};

export default function VendorReviewView({ row, goBack, onStatusUpdated }) {
  useTranslationSnapshot();
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loading, setLoading] = useState(Boolean(row));
  const [error, setError] = useState("");
  const [attachmentReasons, setAttachmentReasons] = useState([]);

  const handleAttachmentReasonsChange = useCallback((nextAttachmentReasons) => {
    setAttachmentReasons(nextAttachmentReasons);
  }, []);

  useEffect(() => {
    if (!row) return undefined;

    let isCurrentRequest = true;

    const fetchCompanyDetails = async () => {
      const jwtContext = getCompanyContextFromJwt();
      const companyId = getCompanyIdFromRow(row) || jwtContext.companyId;
      const businessProfileID =
        getBusinessProfileIdFromRow(row) || jwtContext.businessProfileID;

      if (!companyId || !businessProfileID) {
        const missingFields = [
          !companyId && "company ID",
          !businessProfileID && "business profile ID",
        ]
          .filter(Boolean)
          .join(" and ");

        setLoading(false);
        setError(`${missingFields} is missing for this application.`);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await companyController.getCompanyDetails({
          copmayRequest: {
            businessProfileID,
            companyId,
          },
          jwt: getStoredToken(),
        });

        if (
          response instanceof Error ||
          response?.isAxiosError ||
          response?.response
        ) {
          throw response;
        }

        const payload = getPayload(response);
        if (!payload || typeof payload !== "object") {
          throw new Error("Company details were not returned.");
        }

        if (isCurrentRequest) {
          setCompanyDetails(payload);
        }
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setCompanyDetails(null);
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load company details.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchCompanyDetails();

    return () => {
      isCurrentRequest = false;
    };
  }, [row]);

  const reviewData = useMemo(() => {
    const storedUserEmail = getStoredUserEmail();
    const companyId =
      getCompanyIdFromRow(companyDetails) || getCompanyIdFromRow(row);
    const applicationId =
      [
        getApplicationIdFromRow(row),
        getApplicationIdFromRow(companyDetails),
      ].find((value) => isPresent(value) && value !== companyId) || "";

    return {
      ...row,
      ...companyDetails,
      company: {
        ...(row?.company || {}),
        ...(companyDetails?.company || {}),
      },
      companyDetails: {
        ...(row?.companyDetails || {}),
        ...(companyDetails?.companyDetails || {}),
      },
      applicationId,
      requestId: applicationId,
      id: applicationId || row?.id,
      ownerName: firstDefined(
        row?.ownerName,
        row?.applicantName,
        companyDetails?.ownerName,
        companyDetails?.applicantName,
        companyDetails?.owner?.fullName,
        companyDetails?.owner?.name,
      ),
      ownerUserId: firstDefined(
        row?.ownerUserId,
        row?.ownerId,
        row?.ownerID,
        companyDetails?.ownerUserId,
        companyDetails?.ownerId,
        companyDetails?.ownerID,
        companyDetails?.owner?.userId,
        companyDetails?.owner?.id,
      ),
      emailAddress: firstEmailDifferentFrom(
        storedUserEmail,
        row?.vendorEmail,
        row?.vendorEmailAddress,
        row?.ownerEmail,
        row?.ownerEmailAddress,
        row?.applicantEmail,
        row?.applicantEmailAddress,
        row?.contactEmail,
        row?.contactEmailAddress,
        row?.companyEmail,
        row?.businessEmail,
        row?.company?.vendorEmail,
        row?.company?.ownerEmail,
        row?.company?.ownerEmailAddress,
        row?.company?.emailAddress,
        row?.company?.email,
        companyDetails?.vendorEmail,
        companyDetails?.vendorEmailAddress,
        companyDetails?.ownerEmail,
        companyDetails?.ownerEmailAddress,
        companyDetails?.applicantEmail,
        companyDetails?.applicantEmailAddress,
        companyDetails?.contactEmail,
        companyDetails?.contactEmailAddress,
        companyDetails?.companyEmail,
        companyDetails?.businessEmail,
        companyDetails?.owner?.emailAddress,
        companyDetails?.owner?.email,
        companyDetails?.company?.vendorEmail,
        companyDetails?.company?.ownerEmail,
        companyDetails?.company?.ownerEmailAddress,
        companyDetails?.company?.emailAddress,
        companyDetails?.company?.email,
        row?.emailAddress,
        row?.email,
      ),
      phoneNumber: firstDefined(
        formatPhone(companyDetails?.phoneNumber, companyDetails?.suffix),
        formatPhone(row?.phoneNumber, row?.suffix),
        companyDetails?.registrationDTO?.phoneNumber,
        row?.registrationDTO?.phoneNumber,
        companyDetails?.registeredUser?.phoneNumber,
        row?.registeredUser?.phoneNumber,
        companyDetails?.user?.phoneNumber,
        row?.user?.phoneNumber,
        companyDetails?.userDetails?.phoneNumber,
        row?.userDetails?.phoneNumber,
        companyDetails?.company?.phoneNumber,
        row?.company?.phoneNumber,
        companyDetails?.companyDetails?.phoneNumber,
        row?.companyDetails?.phoneNumber,
        companyDetails?.owner?.phoneNumber,
        row?.owner?.phoneNumber,
        companyDetails?.applicant?.phoneNumber,
        row?.applicant?.phoneNumber,
        [
          companyDetails?.registrationDTO?.phoneNumber,
          companyDetails?.registrationDTO?.phoneCountryCode,
        ],
        [
          row?.registrationDTO?.phoneNumber,
          row?.registrationDTO?.phoneCountryCode,
        ],
        [row?.vendorPhoneNumber, row?.vendorPhoneCountryCode],
        [row?.vendorPhone, row?.vendorPhoneCountryCode],
        row?.vendorMobileNumber,
        row?.vendorMobile,
        row?.ownerPhoneNumber,
        row?.ownerPhone,
        row?.ownerMobileNumber,
        row?.ownerMobile,
        row?.ownerPhoneNb,
        row?.ownerPhoneNo,
        row?.applicantPhoneNumber,
        row?.applicantPhone,
        row?.applicantMobileNumber,
        row?.applicantMobile,
        row?.applicantPhoneNb,
        row?.applicantPhoneNo,
        row?.contactPhoneNumber,
        row?.contactPhone,
        row?.contactNumber,
        row?.contactNo,
        row?.mobileNumber,
        row?.mobile,
        row?.phoneNb,
        row?.phoneNo,
        row?.telephone,
        row?.phone,
        [row?.company?.phoneNumber, row?.company?.phoneCountryCode],
        row?.company?.ownerPhoneNumber,
        row?.company?.ownerPhone,
        row?.company?.contactPhoneNumber,
        row?.company?.mobileNumber,
        [
          companyDetails?.vendorPhoneNumber,
          companyDetails?.vendorPhoneCountryCode,
        ],
        [companyDetails?.vendorPhone, companyDetails?.vendorPhoneCountryCode],
        companyDetails?.vendorMobileNumber,
        companyDetails?.vendorMobile,
        companyDetails?.ownerPhoneNumber,
        companyDetails?.ownerPhone,
        companyDetails?.ownerMobileNumber,
        companyDetails?.ownerMobile,
        companyDetails?.ownerPhoneNb,
        companyDetails?.ownerPhoneNo,
        companyDetails?.applicantPhoneNumber,
        companyDetails?.applicantPhone,
        companyDetails?.applicantMobileNumber,
        companyDetails?.applicantMobile,
        companyDetails?.applicantPhoneNb,
        companyDetails?.applicantPhoneNo,
        companyDetails?.contactPhoneNumber,
        companyDetails?.contactPhone,
        companyDetails?.contactNumber,
        companyDetails?.contactNo,
        companyDetails?.mobileNumber,
        companyDetails?.mobile,
        companyDetails?.phoneNb,
        companyDetails?.phoneNo,
        companyDetails?.telephone,
        companyDetails?.owner?.phone,
        [
          companyDetails?.company?.phoneNumber,
          companyDetails?.company?.phoneCountryCode,
        ],
        companyDetails?.company?.ownerPhoneNumber,
        companyDetails?.company?.ownerPhone,
        companyDetails?.company?.contactPhoneNumber,
        companyDetails?.company?.mobileNumber,
        findPhoneInObject(row),
        findPhoneInObject(companyDetails),
      ),
      storeName:
        companyDetails?.companyName ||
        companyDetails?.company?.companyName ||
        companyDetails?.companyDetails?.companyName ||
        row?.storeName,
    };
  }, [companyDetails, row]);

  if (!row) {
    return (
      <div className="vendor-review-view-wrapper">
        <button
          className="vendor-review-view-back-btn"
          type="button"
          onClick={goBack}
        >
          <ArrowleftIcon />
          <span>{translateLabel("txt_admin_back_to_home", "Back to Home")}</span>
        </button>
        <div className="vendor-review-view-section">
          <div className="vendor-review-view-section-title">
            No vendor selected
          </div>
          <div className="vendor-review-view-section-value">
            Select a vendor application to review its details.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-review-view-wrapper">
      <div className="vendor-review-view-header">
        <button
          className="vendor-review-view-back-btn"
          type="button"
          onClick={goBack}
        >
          <ArrowleftIcon />
          <span>{translateLabel("txt_admin_back_to_home", "Back to Home")}</span>
        </button>
        <div>
          <div className="vendor-review-view-title">
            {reviewData.storeName || translateLabel("txt_admin_company_review", "Company Review")}
          </div>
          <div className="vendor-review-view-app-id">
            Application ID: {reviewData.applicationId || "-"}
          </div>
        </div>
      </div>

      {loading && (
        <div className="vendor-review-view-state">
          Loading company details...
        </div>
      )}

      {!loading && error && (
        <div className="vendor-review-view-state vendor-review-view-state-error">
          {translateText(error)}
        </div>
      )}

      {!loading && !error && companyDetails && (
        <div className="vendor-review-view-body">
          <VendorReviewDetails
            row={reviewData}
            onAttachmentReasonsChange={handleAttachmentReasonsChange}
          />
          <VendorReviewActions
            attachmentReasons={attachmentReasons}
            row={reviewData}
            onStatusUpdated={onStatusUpdated}
          />
        </div>
      )}
    </div>
  );
}
